import React, {useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Pagination,
  Select,
  Text,
} from '@tenancy.nz/ui';
import {
  useGetAgencyPropertiesQuery,
  useInitAgencyBySlugQuery,
} from '../../api/main/agency';
import PropertyItem from '../../components/PropertyItem';
import PropertyList from '../../components/PropertyList';
import ListingPartners from '../../components/ListingPartners';
import scrollTo from '../../utils/helpers/scrollTo';
import * as consts from '../../utils/constants';

const PER_PAGE_OPTIONS = [
  {value: 10, label: '10'},
  {value: 20, label: '20'},
  {value: 50, label: '50'},
];

function RemarketingFeature() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sectionRef = useRef(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const {data: {prepared: initPrepared} = {prepared: {}}} =
    useInitAgencyBySlugQuery(id, {skip: !id});
  const {agency, property} = initPrepared;
  const clientCode = agency?.clientCode;

  const {
    data: {prepared: items = [], meta} = {prepared: [], meta: undefined},
    isFetching,
  } = useGetAgencyPropertiesQuery(
    {clientCode, page, limit, exclude: property?.id},
    {skip: !clientCode}
  );

  const total = meta?.total ?? 0;
  const lastPage = meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  const handlePageChange = (_, value) => {
    setPage(value);
    scrollTo(sectionRef.current);
  };

  const handleLimitChange = event => {
    setLimit(Number(event.target.value));
    setPage(1);
    scrollTo(sectionRef.current);
  };

  if (!clientCode) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{my: 10}}>
      <Box
        ref={sectionRef}
        sx={{pb: 6, borderBottom: '1px solid', borderColor: 'grey.300'}}>
        <Heading as="h3" sx={{mb: 6}}>
          Other properties from {agency?.companyName || 'this agency'}
        </Heading>
        <PropertyList
          items={items}
          loading={isFetching}
          renderItem={({
            id: propId,
            rent,
            mediumphoto,
            dateAvailableDisplay,
            ...rest
          }) => (
            <PropertyItem
              {...rest}
              photo={mediumphoto}
              rentAmount={rent}
              dateAvailable={dateAvailableDisplay}
              onView={() => {
                const forwarded = new URLSearchParams(searchParams);
                forwarded.delete(consts.URL_PARAM_VIEWING);
                forwarded.delete(consts.URL_PARAM_REFERRER);
                const qs = forwarded.toString();
                navigate(`/listing/${propId}${qs ? `?${qs}` : ''}`, {
                  replace: true,
                });
              }}
            />
          )}
        />
        {lastPage > 1 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 4,
              flexWrap: 'wrap',
              mt: 8,
            }}>
            <Box sx={{flex: 1}} />
            <Pagination
              count={lastPage}
              page={page}
              onChange={handlePageChange}
            />
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 8,
              }}>
              <Text>
                {rangeStart}-{rangeEnd} of {total} results
              </Text>
              <Box sx={{width: 96}}>
                <Select
                  hideLabel
                  label="Per page"
                  name="per_page"
                  value={limit}
                  onChange={handleLimitChange}
                  options={PER_PAGE_OPTIONS}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Box sx={{mt: 10}}>
        <ListingPartners />
      </Box>
    </Container>
  );
}

export default RemarketingFeature;
